import Modal from "../Modal";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { Icon } from "../Icon";
import Button from "../Button";
import { FaPen } from "react-icons/fa6";
import { formatDateWithDay, formatTime } from "../../lib/utils";
import ActivityDetails from "../../pages/projects/pages/project/components/Schedule/components/ActivityDetails";
import useEventDetails from "../../pages/events/pages/event/hooks/useEventDetails";
import { ChevronRightIcon, ClockIcon } from "@heroicons/react/24/outline";
import useNav from "../../hooks/useNavigation";
import useActivityById from "../../pages/projects/pages/project/components/Schedule/hooks/useActivityById";
import FullLoading from "../FullLoading";
import ActivityModal from "../../pages/projects/pages/project/components/EventPlanning/components/ActivityModal";
import { useState } from "react";

interface ActivityDetailsModalProps {
  activityId: string;
  eventId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ActivityDetailsModal = ({
  activityId,
  eventId,
  isOpen,
  onClose,
}: ActivityDetailsModalProps) => {
  const { currentOrganization } = useUser();
  const navigateWithLog = useNav();

  const [modifyActivity, setModifyActivity] = useState(false);

  const { data: eventDetailsData } = useEventDetails(eventId);
  const { data: activityByIdData, loading: activityByIdLoading } =
    useActivityById(activityId);

  const linkedEventComponent = () => {
    if (!eventDetailsData?.event || !eventDetailsData.event?.id) return null;

    return (
      <div className="flex flex-col gap-2">
        <div className="font-bold text-sm text-primary">Pour cet évènement</div>

        <div
          className="flex justify-between items-center gap-1 bg-card border border-border rounded-md p-4 cursor-pointer hover:bg-card/50 transition-all duration-300"
          onClick={() => {
            onClose();
            const event = eventDetailsData.event;
            if (event?.projectId) {
              // Use window.location to force full navigation with query params
              // React Router's navigate doesn't always sync properly with nuqs
              window.location.href = `/projects/${event.projectId}?eventId=${event.id}&tab=events`;
            } else {
              navigateWithLog(`/events/${event?.id}`);
            }
          }}
        >
          <div className="flex flex-col gap-2">
            <div className="font-bold text-sm text-primary">
              {eventDetailsData.event.name}
            </div>
            <div className="text-sm text-secondary">
              {formatDateWithDay(eventDetailsData.event.startDate)}
            </div>
          </div>

          <div>
            <ChevronRightIcon className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  };

  if (activityByIdLoading) {
    return (
      <Modal isModalOpen={isOpen} onClose={onClose} title="Chargement...">
        <FullLoading />
      </Modal>
    );
  }

  const activity = activityByIdData?.activity;
  if (!activity) {
    return (
      <Modal
        isModalOpen={isOpen}
        onClose={onClose}
        title="Activité non trouvée"
      >
        <div className="text-sm text-secondary">Aucune activité trouvée</div>
      </Modal>
    );
  }

  return (
    <>
      <ActivityModal
        isOpen={modifyActivity}
        onClose={() => setModifyActivity(false)}
        eventId={activity.eventId ?? undefined}
        defaultActivity={activity}
      />

      <Modal
        isModalOpen={isOpen}
        className="min-w-2xl"
        onClose={onClose}
        title={
          <div>
            <div className="text-white flex items-center gap-2">
              <Icon icon={activity.icon} className="w-4 h-4" />
              {activity.title}

              {activity.createdByUserOrganization?.organizationId ===
              currentOrganization?.id ? (
                <Button
                  variant="secondary"
                  className="flex gap-2 items-center border-0"
                  onClick={() => {
                    onClose();
                    setModifyActivity(true);
                  }}
                >
                  <FaPen className="w-3 h-3" />
                  <div className="text-sm">Modifier</div>
                </Button>
              ) : null}
            </div>
            <div className="flex text-primary items-center gap-2 mt-4">
              <div>{formatDateWithDay(activity.startDate)}</div>
              <div className="text-secondary">•</div>
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-secondary" />
                {formatTime(activity.startDate)}{" "}
                {activity.endDate ? `- ${formatTime(activity.endDate)}` : ""}
              </div>
            </div>
          </div>
        }
      >
        <ActivityDetails activityId={activity.id} className="bg-input" />

        {linkedEventComponent()}
      </Modal>
    </>
  );
};
